/**
 * Focus + tab state for the Workloads page, encoded as URL search params.
 *
 * Shape:
 *   ?focus=service:1&tab=routing
 *   ?focus=pool:3&tab=replicas
 *   ?focus=pod:5&tab=info
 *   (no params)         -> focus=global, tab=dashboard
 *
 * Note on the now-removed "gpu" focus type: the previous version of
 * the tree interposed a virtual "GPU group" node between Service and
 * Pool. That redundancy is gone — a Pool *is* a same-shape group by
 * definition (`accelerators` / `cpus` are pool-level resource
 * selectors), so the focus type lives only on Service / Pool / Pod.
 */

export type FocusType = "global" | "service" | "pool" | "pod";

export interface Focus {
  type: FocusType;
  /** id is meaningless when type === "global". */
  id: number | null;
}

export const GLOBAL_FOCUS: Focus = { type: "global", id: null };

export function parseFocus(value: string | null): Focus {
  if (!value) return GLOBAL_FOCUS;
  const parts = value.split(":");
  const type = parts[0];
  if (type === "global") return GLOBAL_FOCUS;
  if (type !== "service" && type !== "pool" && type !== "pod") {
    return GLOBAL_FOCUS;
  }
  const id = Number(parts[1]);
  if (!Number.isFinite(id)) return GLOBAL_FOCUS;
  return { type, id };
}

export function serializeFocus(focus: Focus): string | null {
  if (focus.type === "global") return null;
  if (focus.id === null) return null;
  return `${focus.type}:${focus.id}`;
}

export function focusEquals(a: Focus, b: Focus): boolean {
  return a.type === b.type && a.id === b.id;
}
